import { useQuery } from '@tanstack/react-query';
import { Download, Loader2, Printer } from 'lucide-react';
import { useState } from 'react';
import { BarList, VolumeChart, formatDuration, humanise } from '../components/charts';
import { HelpHint } from '../components/HelpHint';
import { BusinessReport } from '../components/BusinessReport';
import { api } from '../lib/api';
import { ScrollX } from '../components/ScrollX';

/**
 * Reports for a period.
 *
 * Printing is the export. The browser already renders this page; a print
 * stylesheet turns it into a PDF that keeps the tables and drops the chrome,
 * which is a better document than anything a server-side renderer would
 * produce and costs no dependency.
 *
 * CSV is a separate download because a spreadsheet is what actually happens to
 * this data — someone will want to sort it by technician.
 */
type View = 'period' | 'business';

/**
 * The two questions this page answers are different in kind, so they are not
 * on one screen: "how did the last month go" is operational, and "what does
 * this business look like" is what somebody outside it asks.
 */
function Tabs({ view, onChange }: { view: View; onChange: (next: View) => void }) {
  return (
    <div className="flex gap-1 rounded-xl bg-muted p-1">
      {(
        [
          ['period', 'This period'],
          ['business', 'The business'],
        ] as const
      ).map(([id, label]) => (
        <button
          key={id}
          type="button"
          onClick={() => onChange(id)}
          aria-pressed={view === id}
          className={`rounded-lg px-3 py-1.5 text-sm font-semibold transition-colors ${
            view === id
              ? 'bg-card text-primary-700 shadow-card'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

export function ReportsPage() {
  const [view, setView] = useState<View>('period');
  const today = new Date().toISOString().slice(0, 10);
  const monthAgo = new Date(Date.now() - 30 * 86_400_000).toISOString().slice(0, 10);

  const [from, setFrom] = useState(monthAgo);
  const [to, setTo] = useState(today);

  const { data, isPending, isError, error } = useQuery({
    queryKey: ['report', from, to],
    queryFn: () => api.systemReport(from, to),
  });

  if (view === 'business') {
    return (
      <div className="space-y-5 pb-10">
        <div className="no-print flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className="font-heading text-2xl">Reports</h2>
            <p className="mt-0.5 text-sm text-muted-foreground">
              The business as a whole, over months.
            </p>
          </div>
          <Tabs view={view} onChange={setView} />
        </div>
        <BusinessReport />
      </div>
    );
  }

  return (
    <div className="space-y-5 pb-10">
      <div className="no-print flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-heading text-2xl">Reports</h2>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Volumes, SLA compliance and where the work went.
          </p>
          <div className="mt-3">
            <Tabs view={view} onChange={setView} />
          </div>
        </div>

        <div className="flex flex-wrap items-end gap-2">
          <label className="text-sm">
            <span className="mb-1 block text-xs text-muted-foreground">From</span>
            <input type="date" className="input" value={from} max={to} onChange={(e) => setFrom(e.target.value)} />
          </label>
          <label className="text-sm">
            <span className="mb-1 block text-xs text-muted-foreground">To</span>
            <input type="date" className="input" value={to} min={from} max={today} onChange={(e) => setTo(e.target.value)} />
          </label>
          <button type="button" className="btn-secondary" onClick={() => window.print()}>
            <Printer className="h-4 w-4" aria-hidden="true" />
            Print or save as PDF
          </button>
          <a className="btn-secondary" href={api.systemCsvUrl(from, to)} download>
            <Download className="h-4 w-4" aria-hidden="true" />
            CSV
          </a>
        </div>
      </div>

      {isPending && <Loader2 className="mx-auto mt-16 h-6 w-6 animate-spin text-primary-500" />}
      {isError && <p className="text-danger-600">{(error as Error).message}</p>}

      {data && (
        <>
          {/* Only appears on paper: a printed page has to say what it is. */}
          <div className="print-only mb-4">
            <h1 className="text-xl font-bold">GoSpeedNet — service report</h1>
            <p className="text-sm">
              {new Date(data.period.from).toLocaleDateString('en-KE')} to{' '}
              {new Date(data.period.to).toLocaleDateString('en-KE')} ·{' '}
              generated {new Date(data.generatedAt).toLocaleString('en-KE')}
            </p>
          </div>

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Stat label="Tickets closed" value={data.totals.closed} />
            <Stat
              label="Met their SLA"
              value={data.totals.compliance === null ? '—' : `${data.totals.compliance}%`}
              note={`${data.totals.breached} breached`}
            />
            <Stat label="Median resolution" value={formatDuration(data.totals.medianResolutionMs)} />
            <Stat
              label="Satisfaction"
              value={data.totals.satisfaction === null ? '—' : `${data.totals.satisfaction}/5`}
              note={`${data.totals.ratingCount} rating${data.totals.ratingCount === 1 ? '' : 's'}`}
            />
          </div>

          <section className="card">
            <h3 className="mb-1 font-heading text-lg">Work in and out</h3>
            <VolumeChart data={data.volume} />
          </section>

          <div className="grid gap-5 lg:grid-cols-2">
            <section className="card">
              <h3 className="mb-3 font-heading text-lg">By job type</h3>
              <BarList data={data.byJobType} limit={10} />
            </section>
            <section className="card">
              <h3 className="mb-3 font-heading text-lg">By area</h3>
              <BarList data={data.byArea} limit={10} />
            </section>
            <section className="card">
              <h3 className="mb-3 font-heading text-lg">Closed per technician</h3>
              <BarList data={data.byTechnician} limit={10} />
            </section>
            <section className="card">
              <h3 className="mb-3 flex items-center gap-1.5 font-heading text-lg">
                Root causes
                <HelpHint>
                  From the fixed list a technician picks on completion. This is the reason typed
                  free-text notes were never allowed — it is the only view that answers &quot;what
                  keeps breaking?&quot;
                </HelpHint>
              </h3>
              <BarList
                data={data.byRootCause.map((r) => ({ ...r, label: humanise(r.label) }))}
                limit={10}
              />
            </section>
          </div>

          {data.breachedTickets.length > 0 && (
            <section className="card">
              <h3 className="mb-3 font-heading text-lg">
                Breached in this period ({data.breachedTickets.length})
              </h3>
              <ScrollX className="-mx-4 sm:mx-0">
                <table className="w-full text-sm">
                  <thead className="border-b border-border text-left">
                    <tr>
                      <th className="px-4 py-2 font-semibold">Ticket</th>
                      <th className="px-4 py-2 font-semibold">Priority</th>
                      <th className="px-4 py-2 font-semibold">Area</th>
                      <th className="px-4 py-2 font-semibold">Technician</th>
                      <th className="px-4 py-2 font-semibold">Took</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {data.breachedTickets.map((t) => (
                      <tr key={t.reference}>
                        <td className="px-4 py-2">
                          <span className="font-medium">{t.reference}</span>
                          <span className="ml-2 text-muted-foreground">{t.title}</span>
                        </td>
                        <td className="px-4 py-2">{t.priority}</td>
                        <td className="px-4 py-2 text-muted-foreground">{t.area ?? '—'}</td>
                        <td className="px-4 py-2 text-muted-foreground">{t.technician ?? '—'}</td>
                        <td className="whitespace-nowrap px-4 py-2 tabular-nums">
                          {formatDuration(t.resolutionMs)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </ScrollX>
            </section>
          )}

          {data.ratings.filter((r) => r.comment).length > 0 && (
            <section className="card">
              <h3 className="mb-3 font-heading text-lg">What customers said</h3>
              <ul className="space-y-3 text-sm">
                {data.ratings
                  .filter((r) => r.comment)
                  .map((r, i) => (
                    <li key={`${r.reference}-${i}`} className="border-l-2 border-border pl-3">
                      <span className="font-medium">{r.score}/5</span>
                      <span className="ml-2 text-muted-foreground">{r.reference}</span>
                      <p className="mt-0.5">{r.comment}</p>
                    </li>
                  ))}
              </ul>
            </section>
          )}
        </>
      )}
    </div>
  );
}

function Stat({ label, value, note }: { label: string; value: string | number; note?: string }) {
  return (
    <div className="card">
      <div className="text-sm text-muted-foreground">{label}</div>
      <div className="mt-1 text-3xl font-semibold tabular-nums">{value}</div>
      {note && <div className="mt-0.5 text-xs text-muted-foreground">{note}</div>}
    </div>
  );
}
